import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { LogOut, ArrowLeft, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if user is already authenticated
  const existingToken = localStorage.getItem('session_token');
  const existingUser = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();

  const goHome = () => {
    if (!existingUser) return navigate('/');
    const r = existingUser.role;
    navigate(r === 'admin' ? '/admin' : r === 'host' ? '/dashboard' : '/my-tickets');
  };

  const handleLogout = () => {
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    toast.success('Logged out');
    navigate('/login', { replace: true });
  };

  // If already logged in, show options instead of the login form
  if (existingToken && existingUser) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505] px-4">
        <div className="w-full max-w-sm space-y-6" data-testid="already-logged-in">
          <div className="text-center">
            <img src="https://customer-assets.emergentagent.com/job_brandedticket/artifacts/xh5dnoz3_Screenshot_20260403_164953_Gallery.jpg" alt="VelvetGate" className="h-14 mx-auto mb-6" />
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
              <h1 className="text-xl font-bold text-[#EAE7E1]">You're signed in</h1>
            </div>
            <p className="text-sm text-[#AFAFAF]">as <span className="text-[#EAE7E1] font-medium">{existingUser.email}</span></p>
          </div>
          <div className="space-y-2">
            <Button onClick={goHome} className="w-full bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold py-3" data-testid="go-home-button">
              <ArrowLeft className="w-4 h-4 mr-2" />Go to {existingUser.role === 'admin' ? 'Admin' : existingUser.role === 'host' ? 'Dashboard' : 'My Passes'}
            </Button>
            <Button onClick={handleLogout} variant="outline" className="w-full border-[#1A1A1A] text-[#AFAFAF] hover:text-[#C44545] hover:border-[#C44545]/30 rounded-sm py-3" data-testid="logout-from-login">
              <LogOut className="w-4 h-4 mr-2" />Sign Out & Switch Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleJWTAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin ? { email, password } : { email, password };
      const response = await axios.post(`${API}${endpoint}`, payload);
      localStorage.setItem('session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success(isLogin ? 'Logged in successfully' : 'Registered successfully');
      navigate('/home', { state: { user: response.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin + '/home';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505] px-4">
      <div className="w-full max-w-sm space-y-8">
        {/* Logo */}
        <div className="text-center">
          <a href="/" className="inline-block mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_brandedticket/artifacts/xh5dnoz3_Screenshot_20260403_164953_Gallery.jpg"
              alt="VelvetGate"
              className="h-14 mx-auto"
            />
          </a>
          <h1 className="text-2xl font-bold text-[#F3EEE7] tracking-tight">
            {isLogin ? 'Welcome Back' : 'Create Your Account'}
          </h1>
          <p className="mt-2 text-sm text-[#B9B1A6]">
            {isLogin ? 'Sign in to buy passes or manage your events' : 'Buy passes and manage your profile — free to join.'}
          </p>
          {!isLogin && (
            <p className="mt-2 text-xs text-[#777]" data-testid="signup-host-note">
              Want to host events? Apply to host from your account after signup.
            </p>
          )}
        </div>

        <form onSubmit={handleJWTAuth} className="space-y-5" data-testid="auth-form">
          <div>
            <Label htmlFor="email" className="text-[#F3EEE7]">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-[#0A0A0A] border-[#1A1A1A] text-[#F3EEE7] focus:border-[#C6A66B] focus:ring-[#C6A66B]" data-testid="email-input" />
          </div>
          <div>
            <Label htmlFor="password" className="text-[#F3EEE7]">Password</Label>
            <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 bg-[#0A0A0A] border-[#1A1A1A] text-[#F3EEE7] focus:border-[#C6A66B] focus:ring-[#C6A66B]" data-testid="password-input" />
          </div>
          <Button type="submit" className="w-full bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#8C6B1F] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold" disabled={loading} data-testid="submit-button">
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
          </Button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#1A1A1A]"></div></div>
          <div className="relative flex justify-center text-sm"><span className="px-3 bg-[#050505] text-[#777]">or</span></div>
        </div>

        <Button onClick={handleGoogleAuth} variant="outline" className="w-full border-[#1A1A1A] bg-[#0A0A0A] hover:bg-[#111] text-[#F3EEE7] rounded-sm px-6 py-3 font-medium" data-testid="google-auth-button">
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </Button>

        <div className="text-center">
          <button type="button" onClick={() => setIsLogin(!isLogin)} className="text-sm text-[#C6A66B] hover:text-[#D4AF37] font-medium" data-testid="toggle-auth-mode">
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}
